// Presence state — which of the caller's friends are online right now.
//
// Subscribes to the site SSE stream for `friend-presence-changed` and
// refetches /api/friends/presence on each poke. Components (friends
// widget, friends-view) read the cached map.

import { api } from '../lib/api.js';
import { siteEvents } from '../lib/site-events.js';

export class PresenceStateManager {
  constructor() {
    /** @type {Set<() => void>} */
    this._listeners = new Set();
    /** @type {Map<string, 'online'|'offline'>} */
    this.presence = new Map();
    /** @type {boolean} */
    this.loaded = false;
    this._fetching = false;
    /** @type {Array<() => void>} */
    this._unsubscribers = [];
    this._wired = false;
  }

  /** @param {() => void} listener */
  subscribe(listener) {
    this._listeners.add(listener);
    this._wireSiteEvents();
    return () => {
      this._listeners.delete(listener);
      if (this._listeners.size === 0) this._teardownSiteEvents();
    };
  }

  _wireSiteEvents() {
    if (this._wired) return;
    this._wired = true;
    this._unsubscribers.push(siteEvents.subscribe('friend-presence-changed', () => this.refetch()));
    // A friend acceptance / unfriend shifts the friends list; pull a
    // fresh presence snapshot so the navbar count and indicators stay
    // in sync.
    this._unsubscribers.push(siteEvents.subscribe('friend-request', () => this.refetch()));
    this._unsubscribers.push(siteEvents.subscribe('friendship-changed', () => this.refetch()));
  }

  _teardownSiteEvents() {
    for (const u of this._unsubscribers) u();
    this._unsubscribers = [];
    this._wired = false;
  }

  async refetch() {
    if (this._fetching) return;
    this._fetching = true;
    try {
      const data = await api.getFriendsPresence();
      const next = new Map();
      if (data && typeof data === 'object') {
        for (const [userId, status] of Object.entries(data)) {
          next.set(userId, status === 'online' ? 'online' : 'offline');
        }
      }
      this.presence = next;
      this.loaded = true;
      this._notify();
    } catch (err) {
      console.warn('[presence-state] refetch failed', err);
    } finally {
      this._fetching = false;
    }
  }

  /** @param {string} userId */
  isOnline(userId) {
    return this.presence.get(userId) === 'online';
  }

  get onlineCount() {
    let n = 0;
    for (const status of this.presence.values()) if (status === 'online') n++;
    return n;
  }

  _notify() {
    for (const l of this._listeners) {
      try {
        l();
      } catch (err) {
        console.error('[presence-state] listener threw', err);
      }
    }
  }
}

export const presenceState = new PresenceStateManager();
